import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Truck, Plus, Pencil, Trash2, Loader2 } from "lucide-react";

const empty = { name: "", plate: "", category: "installation" };

export default function AdminTrucksPage() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [trucks, setTrucks] = useState(null);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [f, setF] = useState(empty);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/trucks").then(({ data }) => setTrucks(data)).catch(() => setTrucks([]));
  useEffect(() => { load(); }, []);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const openNew = () => { setEditId(null); setF(empty); setOpen(true); };
  const openEdit = (t) => { setEditId(t.id); setF({ name: t.name, plate: t.plate, category: t.category }); setOpen(true); };

  const save = async () => {
    if (!f.name || !f.plate) return toast.error("Name & plate required");
    setBusy(true);
    try {
      if (editId) { await api.patch(`/trucks/${editId}`, f); toast.success("Truck updated"); }
      else { await api.post("/trucks", f); toast.success("Truck added"); }
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/trucks/${id}`); toast.success("Truck removed"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const byCat = (c) => (trucks || []).filter((t) => t.category === c);

  return (
    <div className="mx-auto max-w-5xl">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-stark-ink sm:text-4xl">Fleet</h1>
          <p className="mt-1 text-sm text-stark-muted">Delivery vans & installation vehicles.</p>
        </div>
        <Button onClick={openNew} className="h-11 rounded-xl bg-stark-terracotta font-bold hover:bg-[#bd4f2c]" data-testid="add-truck-btn">
          <Plus className="mr-1.5 h-4 w-4" /> Add truck
        </Button>
      </div>

      {!trucks ? (
        <div className="mt-6 grid gap-3 sm:grid-cols-2">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24 rounded-2xl" />)}</div>
      ) : (
        ["delivery", "installation"].map((cat) => (
          <section key={cat} className="mt-8">
            <h2 className="mb-3 text-sm font-bold uppercase tracking-wide text-stark-muted">{cat} fleet · {byCat(cat).length}</h2>
            <div className="grid gap-3 sm:grid-cols-2">
              {byCat(cat).map((t) => (
                <div key={t.id} className="stark-card flex items-center gap-4 p-4" data-testid={`truck-row-${t.id}`}>
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-stark-tint text-stark-terracotta"><Truck className="h-5 w-5" /></div>
                  <div className="min-w-0 flex-1">
                    <div className="font-bold text-stark-ink">{t.name}</div>
                    <div className="text-xs font-medium text-stark-muted">{t.plate}</div>
                  </div>
                  {isAdmin && (
                    <>
                      <Button size="sm" variant="ghost" onClick={() => openEdit(t)}><Pencil className="h-4 w-4" /></Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild><Button size="sm" variant="ghost" className="text-stark-error"><Trash2 className="h-4 w-4" /></Button></AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader><AlertDialogTitle>Remove {t.name}?</AlertDialogTitle><AlertDialogDescription>Trucks with active jobs can't be removed.</AlertDialogDescription></AlertDialogHeader>
                          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => remove(t.id)} className="bg-stark-error hover:bg-stark-error/90">Remove</AlertDialogAction></AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </>
                  )}
                </div>
              ))}
              {byCat(cat).length === 0 && <div className="text-sm text-stark-muted">No vehicles.</div>}
            </div>
          </section>
        ))
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editId ? "Edit truck" : "Add truck"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Name</Label><Input value={f.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5" data-testid="truck-name-input" /></div>
            <div><Label>Plate</Label><Input value={f.plate} onChange={(e) => set("plate", e.target.value)} className="mt-1.5" data-testid="truck-plate-input" /></div>
            <div>
              <Label>Category</Label>
              <Select value={f.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger className="mt-1.5" data-testid="truck-category-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="installation">Installation</SelectItem><SelectItem value="delivery">Delivery</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-stark-terracotta hover:bg-[#bd4f2c]" disabled={busy} onClick={save} data-testid="truck-save-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
